import { ChevronsLeft, ChevronsRight, LogOut, Menu, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useLang } from '../LangContext';

// localStorage anahtarı — sidebar'ın açık/kapalı durumunu sakla
const STORAGE_KEY = 'sidebar_collapsed';

// Login ekranındaki Atunears SVG logosu
function AtunearsLogo({ size = 28 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="50" cy="50" r="50" fill="#28D2E6" />
      <path d="M 50 14 A 36 36 0 0 0 50 86" fill="none" stroke="#050a10" strokeWidth="8" strokeLinecap="round" />
      <path d="M 50 26 A 24 24 0 0 0 50 74" fill="none" stroke="#050a10" strokeWidth="8" strokeLinecap="round" />
      <path d="M 50 38 A 12 12 0 0 0 50 62" fill="none" stroke="#050a10" strokeWidth="7" strokeLinecap="round" />
      <rect x="50" y="46" width="48" height="8" fill="#050a10" />
      <circle cx="50" cy="50" r="3.4" fill="#28D2E6" />
    </svg>
  );
}

// Kullanıcı isminden iki harfli avatar üretir
function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/[\s._-]+/).filter(Boolean);
  return (parts.slice(0, 2).map((part) => part[0]).join('') || name[0]).toUpperCase();
}

/**
 * Kurumsal yan menü.
 * @param {Object} props
 * @param {string} props.brand - Marka adı (örn. "Atunears")
 * @param {string} [props.logoSrc] - İsteğe bağlı logo görseli (verilirse "A" markı yerine kullanılır)
 * @param {Array<{label:string, items:Array<{to:string, label:string, icon:React.ComponentType}>}>} props.sections - Bölüm + nav öğeleri
 */
export default function Sidebar({ brand = 'Atunears', logoSrc, sections = [] }) {
  const { user, logout } = useAuth();
  const { locale: t } = useLang();

  // Masaüstü dar mod (collapse): yalnız ikon
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  });

  // Mobil drawer: küçük ekranda menüyü açar/kapatır
  const [mobileOpen, setMobileOpen] = useState(false);

  // Collapse durumunu kalıcı kıl
  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
    document.body.dataset.sidebarCollapsed = collapsed ? '1' : '0';
  }, [collapsed]);

  // Mobil drawer açıkken body scroll'unu kilitle
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  // Rota değişince mobil drawer otomatik kapansın
  function handleNavClick() {
    if (mobileOpen) setMobileOpen(false);
  }

  return (
    <>
      {/* Mobilde görünür hamburger butonu */}
      <button
        type="button"
        className="sb-mobile-toggle"
        onClick={() => setMobileOpen(true)}
        aria-label={t.sidebar.openMenu}
      >
        <Menu size={20} />
      </button>

      {/* Mobil overlay arkaplanı */}
      {mobileOpen && <div className="sb-backdrop" onClick={() => setMobileOpen(false)} />}

      <aside className={`sb${collapsed ? ' is-collapsed' : ''}${mobileOpen ? ' is-open' : ''}`}>
        {/* Üst — marka ve kapatma */}
        <div className="sb-head">
          <div className="sb-brand">
            {logoSrc ? (
              <img src={logoSrc} alt={brand} className="sb-brand-logo" />
            ) : (
              <span className="sb-brand-mark" aria-hidden="true"><AtunearsLogo size={28} /></span>
            )}
            <span className="sb-brand-text">{brand}</span>
          </div>
          <button
            type="button"
            className="sb-mobile-close"
            onClick={() => setMobileOpen(false)}
            aria-label={t.sidebar.closeMenu}
          >
            <X size={18} />
          </button>
        </div>

        {/* Kullanıcı kartı */}
        <div className="sb-user" title={user?.email || ''}>
          <div className="sb-avatar">{getInitials(user?.userName)}</div>
          <div className="sb-user-meta">
            <strong>{user?.userName}</strong>
            <span>{user?.email}</span>
          </div>
        </div>

        {/* Navigasyon */}
        <nav className="sb-nav">
          {sections.map((section) => (
            <div className="sb-group" key={section.label}>
              <div className="sb-group-label">{section.label}</div>
              {section.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={handleNavClick}
                    className={({ isActive }) => `sb-link${isActive ? ' is-active' : ''}`}
                    title={collapsed ? item.label : undefined}
                  >
                    <span className="sb-link-icon"><Icon size={18} /></span>
                    <span className="sb-link-text">{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Alt — collapse toggle + çıkış */}
        <div className="sb-foot">
          <button
            type="button"
            className="sb-collapse-btn"
            onClick={() => setCollapsed((value) => !value)}
            title={collapsed ? t.sidebar.expand : t.sidebar.collapse}
            aria-label={collapsed ? t.sidebar.expand : t.sidebar.collapse}
          >
            {collapsed ? <ChevronsRight size={18} /> : <ChevronsLeft size={18} />}
            <span className="sb-link-text">{t.sidebar.collapse}</span>
          </button>
          <button
            type="button"
            className="sb-logout-btn"
            onClick={logout}
            title={t.sidebar.logout}
            aria-label={t.sidebar.logout}
          >
            <LogOut size={18} />
            <span className="sb-link-text">{t.sidebar.logout}</span>
          </button>
        </div>
      </aside>
    </>
  );
}
